import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { X, ExternalLink } from 'lucide-react';
import { Modal } from '../../_shared/components';
import type { EventMember, EventTable, SfdcUser } from '../event-manager-client';
import { formatEventDate } from '../utils/dates';

// ── Helpers ──────────────────────────────────────────────────────────────────

function linkedinUrl(raw: string | null): string | null {
  if (!raw) return null;
  if (raw.startsWith('http')) return raw;
  return `https://linkedin.com/in/${raw.replace(/^\/+/, '')}`;
}

function sfdcContactUrl(contactId: string | null): string | null {
  if (!contactId) return null;
  return `https://clay.lightning.force.com/lightning/r/Contact/${contactId}/view`;
}

function sfdcCampaignMemberUrl(cmId: string | null): string | null {
  if (!cmId) return null;
  return `https://clay.lightning.force.com/lightning/r/CampaignMember/${cmId}/view`;
}

// Fields that trigger SFDC writeback
const SFDC_FIELDS = new Set(['cm_status', 'intel_notes', 'goer_notes', 'primary_met_with', 'secondary_met_with', 'tertiary_met_with', 'follow_up_owner', 'last_follow_up_date']);

const SFDC_LABEL_MAP: Record<string, string> = {
  cm_status: 'Status',
  intel_notes: 'Notes__c (Pre-Event Notes)',
  goer_notes: 'Met_With_Notes__c (Met With Notes)',
  primary_met_with: 'Primary_User_Met_With__c',
  secondary_met_with: 'Secondary_User_Met_With__c',
  tertiary_met_with: 'Tertiary_User_Met_With__c',
  follow_up_owner: 'Follow_Up_Owner__c',
  last_follow_up_date: 'Last_Follow_Up_Date__c',
};

// ── Sub-components ───────────────────────────────────────────────────────────

function ReadOnlyField({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="grid grid-cols-[140px_1fr] py-2 hover:bg-nightshade-50 dark:hover:bg-nightshade-800 rounded-lg px-2 -mx-2 transition-colors">
      <label className="text-xs text-nightshade-500 dark:text-nightshade-400">
        {label}
      </label>
      <p className="text-sm text-nightshade-700 dark:text-nightshade-300">{value || '—'}</p>
    </div>
  );
}

function SidebarSelect({ label, value, options, onChange }: {
  label: string;
  value: string;
  options: { label: string; value: string }[];
  onChange: (val: string) => void;
}) {
  return (
    <div>
      <label className="block text-[11px] font-mono uppercase tracking-wider font-medium text-nightshade-400 dark:text-nightshade-500 mb-0.5">
        {label}
      </label>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full text-sm bg-white dark:bg-nightshade-800 border border-nightshade-200 dark:border-nightshade-600 rounded-lg px-2 py-1.5 text-nightshade-700 dark:text-nightshade-300 focus:outline-none focus:ring-2 focus:ring-blueberry-400"
      >
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function SidebarCheckbox({ label, checked, onChange }: {
  label: string;
  checked: boolean;
  onChange: (val: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${
          checked
            ? 'bg-blueberry-500 border-blueberry-500 text-white'
            : 'border-nightshade-300 dark:border-nightshade-600 hover:border-blueberry-400'
        }`}
      >
        {checked && (
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        )}
      </button>
      <span className="text-sm text-nightshade-700 dark:text-nightshade-300">{label}</span>
    </label>
  );
}

function SidebarTextInput({ label, value, onChange, placeholder }: {
  label: string;
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
}) {
  return (
    <div>
      <label className="block text-[11px] font-mono uppercase tracking-wider font-medium text-nightshade-400 dark:text-nightshade-500 mb-0.5">
        {label}
      </label>
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={e => onChange(e.target.value)}
        className="w-full text-sm bg-white dark:bg-nightshade-800 border border-nightshade-200 dark:border-nightshade-600 rounded-lg px-2 py-1.5 text-nightshade-700 dark:text-nightshade-300 placeholder:text-nightshade-400 focus:outline-none focus:ring-2 focus:ring-blueberry-400"
      />
    </div>
  );
}

function SidebarDateInput({ label, value, onChange }: {
  label: string;
  value: string;
  onChange: (val: string) => void;
}) {
  return (
    <div>
      <label className="block text-[11px] font-mono uppercase tracking-wider font-medium text-nightshade-400 dark:text-nightshade-500 mb-0.5">
        {label}
      </label>
      <input
        type="date"
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full text-sm bg-white dark:bg-nightshade-800 border border-nightshade-200 dark:border-nightshade-600 rounded-lg px-2 py-1.5 text-nightshade-700 dark:text-nightshade-300 focus:outline-none focus:ring-2 focus:ring-blueberry-400"
      />
    </div>
  );
}

function SidebarTextarea({ label, value, onChange, placeholder, rows = 3 }: {
  label: string;
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
  rows?: number;
}) {
  return (
    <div>
      <label className="block text-[11px] font-mono uppercase tracking-wider font-medium text-nightshade-400 dark:text-nightshade-500 mb-0.5">
        {label}
      </label>
      <textarea
        value={value}
        placeholder={placeholder}
        rows={rows}
        onChange={e => onChange(e.target.value)}
        className="w-full text-sm bg-white dark:bg-nightshade-800 border border-nightshade-200 dark:border-nightshade-600 rounded-lg px-2 py-1.5 text-nightshade-700 dark:text-nightshade-300 placeholder:text-nightshade-400 focus:outline-none focus:ring-2 focus:ring-blueberry-400 resize-y"
      />
    </div>
  );
}

function SidebarCombobox({ label, value, options, onChange, placeholder }: {
  label: string;
  value: string;
  options: { label: string; sub?: string; value: string }[];
  onChange: (val: string) => void;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const q = search.toLowerCase();
    return options.filter(o => o.label.toLowerCase().includes(q) || o.sub?.toLowerCase().includes(q));
  }, [search, options]);

  const selectedLabel = options.find(o => o.value === value)?.label ?? '';

  return (
    <div ref={ref} className="relative">
      <label className="block text-[11px] font-mono uppercase tracking-wider font-medium text-nightshade-400 dark:text-nightshade-500 mb-0.5">
        {label}
      </label>
      <button
        type="button"
        onClick={() => { setOpen(!open); setSearch(''); }}
        className="w-full text-left text-sm bg-white dark:bg-nightshade-800 border border-nightshade-200 dark:border-nightshade-600 rounded-lg px-2 py-1.5 text-nightshade-700 dark:text-nightshade-300 focus:outline-none focus:ring-2 focus:ring-blueberry-400 flex items-center justify-between"
      >
        <span className={selectedLabel ? '' : 'text-nightshade-400'}>
          {selectedLabel || placeholder || 'Select...'}
        </span>
        <svg className="w-3.5 h-3.5 text-nightshade-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="absolute z-40 left-0 right-0 mt-1 bg-white dark:bg-nightshade-900 border border-nightshade-200 dark:border-nightshade-700 rounded-lg shadow-lg max-h-56 flex flex-col">
          <div className="p-1.5 border-b border-nightshade-100 dark:border-nightshade-800">
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search..."
              autoFocus
              className="w-full text-xs bg-nightshade-50 dark:bg-nightshade-800 border border-nightshade-200 dark:border-nightshade-600 rounded-md px-2 py-1 text-nightshade-700 dark:text-nightshade-300 placeholder:text-nightshade-400 focus:outline-none focus:ring-1 focus:ring-blueberry-400"
            />
          </div>
          <div className="flex-1 overflow-y-auto">
            {/* Clear option */}
            <button
              type="button"
              onClick={() => { onChange(''); setOpen(false); }}
              className="w-full text-left px-2 py-1.5 text-xs text-nightshade-400 hover:bg-nightshade-50 dark:hover:bg-nightshade-800"
            >
              Clear
            </button>
            {filtered.map(o => (
              <button
                key={o.value}
                type="button"
                onClick={() => { onChange(o.value); setOpen(false); }}
                className={`w-full text-left px-2 py-1.5 hover:bg-nightshade-50 dark:hover:bg-nightshade-800 ${
                  o.value === value ? 'bg-blueberry-50 dark:bg-blueberry-900/20' : ''
                }`}
              >
                <span className="text-xs text-nightshade-700 dark:text-nightshade-300">{o.label}</span>
                {o.sub && <span className="text-[10px] text-nightshade-400 ml-1.5">{o.sub}</span>}
              </button>
            ))}
            {filtered.length === 0 && (
              <p className="text-xs text-nightshade-400 text-center py-3">No results</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Section header ──────────────────────────────────────────────────────────

function SectionHeader({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3 mb-3 mt-1">
      <h4 className="text-[11px] font-mono uppercase tracking-wider font-medium text-nightshade-500 dark:text-nightshade-400 whitespace-nowrap">{label}</h4>
      <div className="h-px flex-1 bg-nightshade-200 dark:bg-nightshade-700" />
    </div>
  );
}

// ── Constants ────────────────────────────────────────────────────────────────

const ROLE_OPTIONS = [
  { label: 'Guest', value: 'guest' },
  { label: 'Host', value: 'host' },
  { label: 'Staff', value: 'staff' },
  { label: 'Partner', value: 'partner' },
];


// ── Main component ───────────────────────────────────────────────────────────

interface EventMemberSidebarProps {
  member: EventMember;
  tables: EventTable[];
  cmStatuses: string[];
  sfdcUsers: SfdcUser[];
  onPatchMember: (memberId: number, fields: Record<string, unknown>) => Promise<void>;
  onAssignTable: (memberId: number, tableId: number | null) => Promise<void>;
  onClose: () => void;
}

export function EventMemberSidebar({
  member, tables, cmStatuses, sfdcUsers,
  onPatchMember, onAssignTable, onClose,
}: EventMemberSidebarProps) {
  const [visible, setVisible] = useState(false);
  const [pending, setPending] = useState<Record<string, unknown>>({});
  const [showSfdcModal, setShowSfdcModal] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);

  // Reset pending when member changes
  useEffect(() => {
    setPending({});
  }, [member.id]);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 200);
  };

  const setField = useCallback((key: string, value: unknown) => {
    setPending(prev => ({ ...prev, [key]: value }));
  }, []);

  // Resolve displayed value: pending overrides member data
  const val = useCallback((key: string): any => {
    if (key in pending) return pending[key];
    return (member as any)[key];
  }, [pending, member]);

  const isDirty = Object.keys(pending).length > 0;

  // Check if any pending fields are SFDC-bound
  const pendingSfdcFields = useMemo(() =>
    Object.keys(pending).filter(k => SFDC_FIELDS.has(k)),
    [pending],
  );

  const tableOptions = [
    { label: 'Unassigned', value: '' },
    ...tables.map(t => ({ label: t.table_name, value: String(t.id) })),
  ];

  // Build SFDC role options for met-with comboboxes
  const roleOptions = useMemo(() =>
    sfdcUsers.map(r => ({ label: r.name, sub: r.roleLabel, value: r.userId })),
    [sfdcUsers],
  );

  const li = linkedinUrl(member.contact_linkedin);
  const sfdcUrl = sfdcContactUrl(member.contact_id);
  const sfdcCmUrl = sfdcCampaignMemberUrl(member.campaign_member_id);

  // ── Save logic ──────────────────────────────────────────────────────────

  const executeSave = useCallback(async () => {
    setShowSfdcModal(false);
    setSaving(true);
    try {
      const { table_id, ...memberFields } = pending;

      // Fire member patch (includes cm_status + SFDC writeback on backend)
      if (Object.keys(memberFields).length > 0) {
        // Convert empty strings to null for nullable fields
        const cleaned: Record<string, unknown> = {};
        for (const [k, v] of Object.entries(memberFields)) {
          if (typeof v === 'string' && v === '' && k !== 'cm_status') {
            cleaned[k] = null;
          } else {
            cleaned[k] = v;
          }
        }
        await onPatchMember(member.id, cleaned);
      }

      // Fire table assignment separately
      if (table_id !== undefined) {
        const tid = table_id ? Number(table_id) : null;
        await onAssignTable(member.id, tid);
      }

      setPending({});
    } catch {
      // Error handled by parent toast — keep failed fields in pending
    } finally {
      setSaving(false);
    }
  }, [pending, member.id, onPatchMember, onAssignTable]);

  const handleSave = useCallback(async () => {
    // If SFDC fields are pending, show confirmation modal
    if (pendingSfdcFields.length > 0) {
      setShowSfdcModal(true);
      return;
    }
    await executeSave();
  }, [pendingSfdcFields, executeSave]);

  return (
    <>
      <div className={`flex-shrink-0 h-full overflow-hidden transition-[width] duration-200 ${visible ? 'w-[400px]' : 'w-0'}`}>
        <div className="w-[400px] h-full border-l border-nightshade-200 dark:border-nightshade-700 bg-white dark:bg-nightshade-900 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex-shrink-0 p-4 border-b border-nightshade-200 dark:border-nightshade-700 bg-white dark:bg-nightshade-900">
            <div className="flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-nightshade-900 dark:text-nightshade-100 truncate">
                  {member.contact_name ?? 'Unknown'}
                </h3>
              </div>
              <button
                onClick={handleClose}
                className="p-1 rounded-full hover:bg-nightshade-100 dark:hover:bg-nightshade-800 transition-colors"
              >
                <X className="w-4 h-4 text-nightshade-400" />
              </button>
            </div>
            {(member.contact_title || member.account_name) && (
              <p className="text-xs text-nightshade-500 dark:text-nightshade-400 truncate mt-0.5">
                {[member.contact_title, member.account_name].filter(Boolean).join(' • ')}
              </p>
            )}
            {(member.gtme_owner_name || member.account_type || member.sales_tier) && (
              <p className="text-xs text-nightshade-400 dark:text-nightshade-500 truncate mt-0.5">
                {[
                  member.gtme_owner_name ? `Owner: ${member.gtme_owner_name}` : null,
                  member.account_type,
                  member.sales_tier,
                ].filter(Boolean).join(' • ')}
              </p>
            )}
            {member.contact_email && (
              <a href={`mailto:${member.contact_email}`}
                className="text-xs text-blueberry-500 hover:text-blueberry-600 dark:text-blueberry-400 dark:hover:text-blueberry-300 truncate mt-0.5 block">
                {member.contact_email}
              </a>
            )}
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              {sfdcUrl && (
                <a href={sfdcUrl} target="_blank" rel="noopener noreferrer"
                  className="text-[10px] text-blueberry-500 hover:text-blueberry-600 flex items-center gap-0.5">
                  SFDC Contact <ExternalLink className="w-2.5 h-2.5" />
                </a>
              )}
              {sfdcCmUrl && (
                <a href={sfdcCmUrl} target="_blank" rel="noopener noreferrer"
                  className="text-[10px] text-blueberry-500 hover:text-blueberry-600 flex items-center gap-0.5">
                  Campaign Member <ExternalLink className="w-2.5 h-2.5" />
                </a>
              )}
              {li && (
                <a href={li} target="_blank" rel="noopener noreferrer"
                  className="text-[10px] text-blueberry-500 hover:text-blueberry-600 flex items-center gap-0.5">
                  LinkedIn <ExternalLink className="w-2.5 h-2.5" />
                </a>
              )}
            </div>
          </div>

          {/* Scrollable body */}
          <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
            {/* Editable: Event Details */}
            <div className="rounded-lg border-l-[3px] border-l-blueberry-400 border border-nightshade-200 dark:border-nightshade-700 p-3 space-y-3">
              <SectionHeader label="Event Details" />
              <SidebarSelect
                label="CM Status"
                value={val('cm_status') ?? ''}
                options={cmStatuses.map(s => ({ label: s, value: s }))}
                onChange={v => setField('cm_status', v)}
              />
              <SidebarSelect
                label="Role"
                value={val('role') ?? 'guest'}
                options={ROLE_OPTIONS}
                onChange={v => setField('role', v)}
              />
              <SidebarSelect
                label="Table"
                value={val('table_id') != null ? String(val('table_id')) : ''}
                options={tableOptions}
                onChange={v => setField('table_id', v || null)}
              />
            </div>

            {/* Editable: Pre-Event */}
            <div className="rounded-lg border-l-[3px] border-l-blueberry-400 border border-nightshade-200 dark:border-nightshade-700 p-3 space-y-3">
              <SectionHeader label="Pre-Event" />
              <SidebarTextarea
                label="Pre-Event Notes"
                value={val('intel_notes') ?? ''}
                placeholder="Pre-event notes..."
                onChange={v => setField('intel_notes', v)}
              />
              <SidebarTextInput
                label="Dietary Restrictions"
                value={val('dietary_restrictions') ?? ''}
                placeholder="None"
                onChange={v => setField('dietary_restrictions', v)}
              />
              <SidebarCheckbox
                label="Owner Exclusion"
                checked={val('owner_exclusion') ?? false}
                onChange={v => setField('owner_exclusion', v)}
              />
              <SidebarCheckbox
                label="Owner Priority"
                checked={val('owner_priority') ?? false}
                onChange={v => setField('owner_priority', v)}
              />
              <SidebarCheckbox
                label="Cal Invite Sent"
                checked={val('cal_invite_sent') ?? false}
                onChange={v => setField('cal_invite_sent', v)}
              />
              <SidebarCheckbox
                label="Cal Accepted"
                checked={val('cal_invite_accepted') ?? false}
                onChange={v => setField('cal_invite_accepted', v)}
              />
            </div>

            {/* Editable: Post-Event */}
            <div className="rounded-lg border-l-[3px] border-l-blueberry-400 border border-nightshade-200 dark:border-nightshade-700 p-3 space-y-3">
              <SectionHeader label="Post-Event" />
              <SidebarCombobox
                label="Met with Claymate 1"
                value={val('primary_met_with') ?? ''}
                options={roleOptions}
                placeholder="Select Claymate..."
                onChange={v => setField('primary_met_with', v || null)}
              />
              <SidebarCombobox
                label="Met with Claymate 2"
                value={val('secondary_met_with') ?? ''}
                options={roleOptions}
                placeholder="Select Claymate..."
                onChange={v => setField('secondary_met_with', v || null)}
              />
              <SidebarCombobox
                label="Met with Claymate 3"
                value={val('tertiary_met_with') ?? ''}
                options={roleOptions}
                placeholder="Select Claymate..."
                onChange={v => setField('tertiary_met_with', v || null)}
              />
              <SidebarTextarea
                label="Met With Notes"
                value={val('goer_notes') ?? ''}
                placeholder="Met with notes..."
                onChange={v => setField('goer_notes', v)}
              />
              <SidebarCombobox
                label="Follow-up Owner"
                value={val('follow_up_owner') ?? ''}
                options={roleOptions}
                placeholder="Select owner..."
                onChange={v => setField('follow_up_owner', v || null)}
              />
              <SidebarDateInput
                label="Follow-up Date"
                value={val('last_follow_up_date') ?? ''}
                onChange={v => setField('last_follow_up_date', v || null)}
              />
            </div>

            {/* Read-only: Account info */}
            <div className="space-y-3">
              <SectionHeader label="Account" />
              <ReadOnlyField label="Email" value={member.contact_email} />
              <ReadOnlyField label="Product Tier" value={member.product_tier} />
              <ReadOnlyField label="Plan Interval" value={member.plan_interval} />
              <ReadOnlyField label="ARR" value={member.arr_active_subscriptions != null ? `$${member.arr_active_subscriptions.toLocaleString()}` : null} />
              <ReadOnlyField label="GS Owner" value={member.gs_owner_name} />
              <ReadOnlyField label="ClayDR" value={member.claydr_name} />
              <ReadOnlyField label="Last GTME Meeting" value={formatEventDate(member.last_gtme_meeting_date)} />
              <ReadOnlyField label="Last GS Meeting" value={formatEventDate(member.last_gs_meeting_date)} />
              <ReadOnlyField label="Last ClayDR Meeting" value={formatEventDate(member.last_claydr_meeting_date)} />
              {member.suppressed && <ReadOnlyField label="Contact Suppressed" value={member.suppression_reasons ?? 'Yes'} />}
              {member.account_suppressed && <ReadOnlyField label="Account Suppressed" value={member.account_suppression_reasons ?? 'Yes'} />}
            </div>
          </div>

          {/* Footer */}
          <div className="flex-shrink-0 p-4 border-t border-nightshade-200 dark:border-nightshade-700 bg-white dark:bg-nightshade-900">
            <button
              disabled={!isDirty || saving}
              onClick={() => { void handleSave(); }}
              className={`w-full py-2 rounded-full text-sm font-medium transition-colors ${
                isDirty && !saving
                  ? 'bg-blueberry-500 hover:bg-blueberry-600 text-white'
                  : 'bg-nightshade-100 dark:bg-nightshade-800 text-nightshade-400 cursor-not-allowed'
              }`}
            >
              {saving ? 'Saving...' : 'Update Record'}
            </button>
          </div>
        </div>
      </div>

      {/* SFDC Confirmation Modal */}
      <Modal
        open={showSfdcModal}
        title="Confirm SFDC Changes"
        onClose={() => setShowSfdcModal(false)}
      >
        <div className="space-y-3">
          <p className="text-sm text-nightshade-700 dark:text-nightshade-300">
            Changing these fields will alter SFDC data. Are you sure?
          </p>
          <ul className="text-sm text-nightshade-600 dark:text-nightshade-400 list-disc list-inside space-y-1">
            {pendingSfdcFields.map(k => (
              <li key={k}>{SFDC_LABEL_MAP[k] ?? k}</li>
            ))}
          </ul>
          <div className="flex gap-2 pt-2 justify-end">
            <button
              onClick={() => setShowSfdcModal(false)}
              className="px-4 py-1.5 text-sm rounded-full border border-nightshade-200 dark:border-nightshade-600 text-nightshade-600 dark:text-nightshade-300 hover:bg-nightshade-50 dark:hover:bg-nightshade-800 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => { void executeSave(); }}
              className="px-4 py-1.5 text-sm rounded-full bg-blueberry-500 text-white hover:bg-blueberry-600 transition-colors"
            >
              Confirm & Save
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
